// src/users/pagos/PagosUsers.js
// ═══════════════════════════════════════════════════════════════════════════
// VERSIÓN UNIFICADA - Estilo basado en UserProfile.js
// Incluye tabs de filtrado y diseño premium UrbanQ
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useMemo } from 'react';
import './PagosUsers.css';
import {
    Row, Col, Card, Table, Button, ProgressBar, Badge, Spinner
} from 'react-bootstrap';
import {
  FaCheckCircle,
  FaHourglassHalf,
  FaUpload,
  FaQrcode,
  FaChartLine,
  FaDollarSign,
  FaExclamationCircle,
  FaPaypal,
  FaList,
  FaClock,
  FaClipboardCheck
} from 'react-icons/fa';
import Pay from '../../portal_pagos/Pay';
import PayQR from '../../portal_pagos/PayQR';
import PayAlicuotaPayPal from '../../portal_pagos/PayAlicuotaPayPal';

// ═══════════════════════════════════════════════════════════════════════════
// FUNCIONES AUXILIARES
// ═══════════════════════════════════════════════════════════════════════════

const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(Number(amount))) return '$0.00';
    const numAmount = Number(amount);
    return `$${numAmount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`;
};

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        const date = new Date(dateString);
        if (dateString.length === 10 && dateString.includes('-')) {
           const parts = dateString.split('-');
           const utcDate = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
           if (isNaN(utcDate.getTime())) return 'Fecha inválida';
           return utcDate.toLocaleDateString('es-EC', { 
               year: 'numeric', 
               month: 'long', 
               day: 'numeric', 
               timeZone: 'UTC' 
           });
        }
        if (isNaN(date.getTime())) return 'Fecha inválida';
        return date.toLocaleDateString('es-EC', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric' 
        });
    } catch (e) {
        console.error("Error formateando fecha:", dateString, e);
        return "Fecha inválida";
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

function PagosUsers({ pagos, stats, loading, onPaymentSuccess, user }) {
    // ===== ESTADOS LOCALES =====
    const [showPayModal, setShowPayModal] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [showPayQRModal, setShowPayQRModal] = useState(false);
    const [selectedPaymentForQR, setSelectedPaymentForQR] = useState(null);
    const [showPayPalModal, setShowPayPalModal] = useState(false);
    const [selectedPaymentForPayPal, setSelectedPaymentForPayPal] = useState(null);

    // Estado para tabs de filtrado
    const [activeTab, setActiveTab] = useState('todos');

    // Filtrar pagos segun el tab activo
    const pagosFiltrados = useMemo(() => {
        switch(activeTab) {
            case 'pendientes':
                return pagos.filter(p => p.estado === 'Pendiente');
            case 'pagados':
                return pagos.filter(p => p.estado === 'Pagado');
            case 'validacion':
                return pagos.filter(p => p.estado === 'Pendiente de validación');
            default:
                return pagos;
        }
    }, [pagos, activeTab]);

    // ===== HANDLERS =====
    
    // Handler para subir comprobante
    const handlePayClick = (payment) => {
        console.log('Abriendo modal de comprobante para:', payment);
        setSelectedPayment(payment);
        setShowPayModal(true);
    };
    
    // Handler para pagar con QR
    const handlePayQRClick = (payment) => {
        console.log('Abriendo modal QR para:', payment);
        setSelectedPaymentForQR(payment);
        setShowPayQRModal(true);
    };

    // ✅ NUEVO: Handler para pagar con PayPal
    const handlePayPalClick = (payment) => {
        console.log('Abriendo modal PayPal para:', payment);
        setSelectedPaymentForPayPal(payment);
        setShowPayPalModal(true);
    };

    // Handler de éxito de pago (para todos los métodos)
    const handlePaymentSuccessInternal = (paymentId, action) => {
        console.log('Pago procesado:', paymentId, 'acción:', action);

        // Cerrar modal de PayPal
        setShowPayPalModal(false);

        // Si la acción es subir comprobante, abrir ese modal
        if (action === 'upload_receipt') {
            const payment = pagos.find(p => p.id === paymentId);
            if (payment) {
                setSelectedPayment(payment);
                setShowPayModal(true);
            }
            return;
        }

        // Cerrar todos los modales para otros casos
        setShowPayModal(false);
        setShowPayQRModal(false);

        // Notificar al padre
        if (onPaymentSuccess) {
            onPaymentSuccess(paymentId);
        }
    };


    // ===== RENDER =====

    return (
        <div className="pagos-users-container urbanq-user-container">
            {/* ===== TABS DE FILTRADO ===== */}
            <div className="urbanq-user-tabs">
                <button
                    className={`urbanq-user-tab ${activeTab === 'todos' ? 'active' : ''}`}
                    onClick={() => setActiveTab('todos')}
                >
                    <FaList /> Todos ({pagos.length})
                </button>
                <button
                    className={`urbanq-user-tab ${activeTab === 'pendientes' ? 'active' : ''}`}
                    onClick={() => setActiveTab('pendientes')}
                >
                    <FaClock /> Pendientes ({stats.pagosPendientes})
                </button>
                <button
                    className={`urbanq-user-tab ${activeTab === 'pagados' ? 'active' : ''}`}
                    onClick={() => setActiveTab('pagados')}
                >
                    <FaCheckCircle /> Pagados ({stats.pagosRealizados})
                </button>
                <button
                    className={`urbanq-user-tab ${activeTab === 'validacion' ? 'active' : ''}`}
                    onClick={() => setActiveTab('validacion')}
                >
                    <FaClipboardCheck /> En Validacion ({stats.pendientesAprobacion})
                </button>
            </div>

            {/* ===== TARJETAS DE ESTADISTICAS ===== */}
            <div className="urbanq-user-stats-grid">
                <div className="urbanq-user-stat-card success">
                    <div className="urbanq-user-stat-icon success">
                        <FaCheckCircle />
                    </div>
                    <div className="urbanq-user-stat-content">
                        <div className="urbanq-user-stat-value">{stats.pagosRealizados}</div>
                        <div className="urbanq-user-stat-label">Pagos Completados</div>
                    </div>
                </div>

                <div className="urbanq-user-stat-card warning">
                    <div className="urbanq-user-stat-icon warning">
                        <FaHourglassHalf />
                    </div>
                    <div className="urbanq-user-stat-content">
                        <div className="urbanq-user-stat-value">{stats.pendientesAprobacion}</div>
                        <div className="urbanq-user-stat-label">En Validacion</div>
                    </div>
                </div>

                <div className="urbanq-user-stat-card danger">
                    <div className="urbanq-user-stat-icon danger">
                        <FaExclamationCircle />
                    </div>
                    <div className="urbanq-user-stat-content">
                        <div className="urbanq-user-stat-value">{stats.pagosPendientes}</div>
                        <div className="urbanq-user-stat-label">Pendientes de Pago</div>
                    </div>
                </div>

                <div className="urbanq-user-stat-card info">
                    <div className="urbanq-user-stat-icon info">
                        <FaChartLine />
                    </div>
                    <div className="urbanq-user-stat-content">
                        <div className="urbanq-user-stat-value">{stats.totalRegistros || stats.total || 0}</div>
                        <div className="urbanq-user-stat-label">Total Registros</div>
                        <ProgressBar
                            now={stats.porcentajeCompletado || stats.progreso || 0}
                            className="mt-2 progress-mini"
                            variant="info"
                        />
                        <small className="text-muted d-block mt-1">
                            {stats.porcentajeCompletado || stats.progreso || 0}% completado
                        </small>
                    </div>
                </div>
            </div>

            {/* ===== TABLA DE PAGOS ===== */}
            <div className="urbanq-user-table-card">
                <div className="urbanq-user-table-header">
                    <FaChartLine />
                    <h3>Historial de Movimientos</h3>
                </div>

                <div className="table-responsive">
                    <Table hover className="urbanq-user-table mb-0">
                        <thead>
                            <tr>
                                <th>Fecha Creacion</th>
                                <th>Concepto</th>
                                <th className="text-end">Monto</th>
                                <th className="text-center">Estado</th>
                                <th className="text-center">Pagar QR</th>
                                <th className="text-center">PayPal</th>
                                <th className="text-center">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && pagos.length > 0 && (
                                <tr>
                                    <td colSpan="7" className="text-center p-4">
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Recargando datos...
                                    </td>
                                </tr>
                            )}

                            {!loading && pagosFiltrados.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="text-center p-5">
                                        <div className="urbanq-user-empty">
                                            <FaDollarSign className="urbanq-user-empty-icon" />
                                            <h4>No hay pagos {activeTab !== 'todos' ? 'en esta categoria' : 'registrados'}</h4>
                                            <p>
                                                {activeTab === 'todos'
                                                    ? 'Tus alicuotas apareceran aqui cuando sean creadas'
                                                    : 'No tienes pagos con este estado actualmente'}
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                !loading && pagosFiltrados.map(pago => (
                                        <tr key={pago.id}>
                                            <td>
                                                {formatDate(pago.created_at)}
                                            </td>
                                            <td>
                                                <div>
                                                    <strong>{pago.concept}</strong>
                                                    {pago.description && (
                                                        <small className="d-block text-muted">
                                                            {pago.description}
                                                        </small>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="text-end">
                                                <strong className="text-primary">
                                                    {formatCurrency(pago.amount)}
                                                </strong>
                                            </td>
                                            <td className="text-center">
                                                <span className={`urbanq-user-badge ${
                                                    pago.estado === 'Pagado'
                                                        ? 'success'
                                                        : pago.estado === 'Pendiente de validación'
                                                        ? 'warning'
                                                        : 'danger'
                                                }`}>
                                                    {pago.estado}
                                                </span>
                                            </td>
                                            
                                            {/* Columna: Pagar QR */}
                                            <td className="text-center">
                                                {pago.estado === 'Pendiente' ? (
                                                    <button
                                                        className="urbanq-user-btn info sm"
                                                        onClick={() => handlePayQRClick(pago)}
                                                        disabled={loading}
                                                        title="Pagar con codigo QR"
                                                    >
                                                        <FaQrcode /> QR
                                                    </button>
                                                ) : (
                                                    <span className="text-muted small">
                                                        {pago.estado === 'Pagado' ? '✓' : '-'}
                                                    </span>
                                                )}
                                            </td>

                                            {/* Columna PayPal */}
                                            <td className="text-center">
                                                {pago.estado === 'Pendiente' ? (
                                                    <button
                                                        className="urbanq-user-btn warning sm"
                                                        onClick={() => handlePayPalClick(pago)}
                                                        disabled={loading}
                                                        title="Pagar con PayPal"
                                                    >
                                                        <FaPaypal /> PayPal
                                                    </button>
                                                ) : (
                                                    <span className="text-muted small">
                                                        {pago.estado === 'Pagado' ? '✓' : '-'}
                                                    </span>
                                                )}
                                            </td>

                                            {/* Columna: Subir Comprobante */}
                                            <td className="text-center">
                                                {pago.estado === 'Pendiente' ? (
                                                    <button
                                                        className="urbanq-user-btn primary sm"
                                                        onClick={() => handlePayClick(pago)}
                                                        disabled={loading}
                                                        title="Subir Comprobante"
                                                    >
                                                        <FaUpload /> Subir
                                                    </button>
                                                ) : pago.estado === 'Pendiente de validación' ? (
                                                    <button
                                                        className="urbanq-user-btn secondary sm"
                                                        disabled
                                                    >
                                                        <FaHourglassHalf /> Revision
                                                    </button>
                                                ) : (
                                                    <button
                                                        className="urbanq-user-btn success sm"
                                                        disabled
                                                    >
                                                        <FaCheckCircle /> Pagado
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>
                </div>

            {/* ═══════════════════════════════════════════════════════════════ */}
            {/* MODALES */}
            {/* ═══════════════════════════════════════════════════════════════ */}
            
            {/* Modal: Subir Comprobante */}
            {showPayModal && selectedPayment && (
                <Pay 
                    show={showPayModal} 
                    onHide={() => setShowPayModal(false)} 
                    payment={selectedPayment} 
                    onPaymentSuccess={handlePaymentSuccessInternal}
                />
            )}
            
            {/* Modal: Pagar con QR */}
            {showPayQRModal && selectedPaymentForQR && (
                <PayQR 
                    show={showPayQRModal} 
                    onHide={() => setShowPayQRModal(false)} 
                    payment={selectedPaymentForQR} 
                    onPaymentSuccess={handlePaymentSuccessInternal}
                />
            )}

            {/* ✅ MODIFICADO: Modal PayPal.me (simple) */}
            {showPayPalModal && selectedPaymentForPayPal && (
                <PayAlicuotaPayPal
                    show={showPayPalModal}
                    onHide={() => setShowPayPalModal(false)}
                    payment={selectedPaymentForPayPal}
                    user={user}
                    onPaymentSuccess={handlePaymentSuccessInternal}
                />
            )}
        </div>
    );
}

export default PagosUsers;